import { useEffect, lazy, Suspense } from 'react'
import { Routes, Route, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { CartProvider } from './context/CartContext.jsx'
import { CustomerProvider, useCustomer } from './context/CustomerContext.jsx'
import { ProductsProvider } from './context/ProductsContext.jsx'
import { ToastProvider } from './context/ToastContext.jsx'
import { useProducts } from './context/ProductsContext.jsx'
import { storeReferralCode, setCustomerToken } from './lib/customerApi.js'
import Layout from './components/Layout.jsx'

// Lazy-loaded: the admin dashboard is a large bundle (SEO editor, order
// management, carrier tracking, etc.) that only staff ever touch — code-
// splitting it out keeps it off the storefront's initial JS payload.
const AdminPage = lazy(() => import('./pages/AdminPage.jsx'))

import HomePage from './pages/HomePage.jsx'
import CatalogPage from './pages/CatalogPage.jsx'
import ProductDetailPage from './pages/ProductDetailPage.jsx'
import CheckoutPage from './pages/CheckoutPage.jsx'
import OrderConfirmPage from './pages/OrderConfirmPage.jsx'
import InvoicePage from './pages/InvoicePage.jsx'
import TrackOrderPage from './pages/TrackOrderPage.jsx'
import TermsPage from './pages/TermsPage.jsx'
import PrivacyPage from './pages/PrivacyPage.jsx'
import StatusPage from './pages/StatusPage.jsx'
import NotFoundPage from './pages/NotFoundPage.jsx'

import AccountPage from './pages/account/AccountPage.jsx'
import AccountAuthPage from './pages/account/AccountAuthPage.jsx'
import AccountConfirmPage from './pages/account/AccountConfirmPage.jsx'

// Keeps the browser tab icon in sync with the branding-configured favicon,
// mounted at the app root (not inside Layout) so it applies on every route,
// including /admin which renders outside Layout.
function FaviconUpdater() {
  const { branding } = useProducts()
  useEffect(() => {
    const faviconUrl = branding?.favicon?.url
    if (!faviconUrl) return
    let link = document.getElementById('dynamic-favicon')
    if (!link) {
      link = document.createElement('link')
      link.id = 'dynamic-favicon'
      link.rel = 'icon'
      document.head.appendChild(link)
    }
    link.href = faviconUrl
  }, [branding?.favicon?.url])
  return null
}

// Consumes a staff-issued "?impersonate=<token>" link from any page (the
// admin opens these at the site root, not under /account) — adopts the
// session, strips the query param, and lands on the account dashboard.
function ImpersonationHandler() {
  const [params] = useSearchParams()
  const { refresh } = useCustomer()
  const navigate = useNavigate()
  useEffect(() => {
    const token = params.get('impersonate')
    if (!token) return
    setCustomerToken(token)
    refresh().then(() => navigate('/account', { replace: true }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params])
  return null
}

// Companion to the ProductsContext preview bridge: the admin's Site Content
// live preview also posts a "scroll to this section" message when the
// staff member switches sub-tabs, so the (persistent, not reloaded) iframe
// jumps to the right part of the page. No-op unless actually framed.
function PreviewScrollBridge() {
  useEffect(() => {
    if (window.self === window.top) return
    function onMessage(e) {
      if (e.origin !== window.location.origin) return
      if (e.data?.type !== 'lookslab-preview-scroll') return
      const target = e.data.target
      if (!target || target === 'top') window.scrollTo({ top: 0, behavior: 'instant' })
      else document.getElementById(target)?.scrollIntoView({ behavior: 'instant', block: 'start' })
    }
    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [])
  return null
}

/** /r/CODE — stores the referral code then redirects home; mirrors Prod's
 * inline ReferralLanding in App.jsx exactly (no visible UI). */
function ReferralLanding() {
  const { code } = useParams()
  const navigate = useNavigate()
  useEffect(() => {
    if (code) storeReferralCode(code)
    navigate('/', { replace: true })
  }, [code, navigate])
  return null
}

export default function App() {
  return (
    <ToastProvider>
      <CustomerProvider>
        <ProductsProvider>
        <CartProvider>
          <FaviconUpdater />
          <ImpersonationHandler />
          <PreviewScrollBridge />
          <Routes>
            <Route element={<Layout />}>
              <Route path="/" element={<HomePage />} />
              <Route path="/catalog" element={<CatalogPage />} />
              <Route path="/peptides/:id" element={<ProductDetailPage />} />
              <Route path="/checkout" element={<CheckoutPage />} />
              <Route path="/track" element={<TrackOrderPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/status" element={<StatusPage />} />

              <Route path="/account/login" element={<AccountAuthPage mode="login" />} />
              <Route path="/account/register" element={<AccountAuthPage mode="register" />} />
              <Route path="/account/forgot" element={<AccountAuthPage mode="forgot" />} />
              <Route path="/account/confirm" element={<AccountConfirmPage />} />
              <Route path="/account" element={<AccountPage />} />

              <Route path="*" element={<NotFoundPage />} />
            </Route>

            {/* No site nav for these — chrome-less, mirrors Prod exactly. */}
            <Route path="/r/:code" element={<ReferralLanding />} />
            <Route path="/order-confirm" element={<OrderConfirmPage />} />
            <Route path="/invoice" element={<InvoicePage />} />
            <Route path="/admin" element={<Suspense fallback={null}><AdminPage /></Suspense>} />
          </Routes>
        </CartProvider>
        </ProductsProvider>
      </CustomerProvider>
    </ToastProvider>
  )
}
